import { useEffect, useState } from "react";
import { useMe } from "../../../lib/me";
import { research, type Sender } from "../lib/research";
import { useBusiness } from "../lib/business";

interface Draft {
  id?: number;
  business_id: number | "";
  label: string; from_name: string; from_email: string; reply_to: string;
  resend_api_key: string; webhook_secret: string;
}
const EMPTY: Draft = { business_id: "", label: "", from_name: "", from_email: "", reply_to: "", resend_api_key: "", webhook_secret: "" };

export default function Senders() {
  const me = useMe();
  const isAdmin = me?.role === "admin";
  const { businesses } = useBusiness();
  const companyName = (id: number) => businesses.find(b => b.id === id)?.name || `#${id}`;
  const [senders, setSenders] = useState<Sender[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => research.senders().then(d => setSenders(d.senders)).catch(e => setError(String(e)));
  useEffect(() => { load(); }, []);

  async function save() {
    if (!draft) return;
    if (!draft.business_id) { setError("Choose the company this sender belongs to."); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const body = {
        business_id: Number(draft.business_id), label: draft.label, from_name: draft.from_name, from_email: draft.from_email, reply_to: draft.reply_to,
        ...(draft.resend_api_key.trim() ? { resend_api_key: draft.resend_api_key.trim() } : {}),
        ...(draft.webhook_secret.trim() ? { webhook_secret: draft.webhook_secret.trim() } : {}),
      };
      const r = draft.id
        ? await research.updateSender(draft.id, body)
        : await research.createSender({ ...body, resend_api_key: draft.resend_api_key.trim() });
      setNotice(r.warning ? `Saved. ${r.warning}` : "Saved — the key was checked against Resend.");
      setDraft(null);
      load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Sender accounts</h1>
          <p className="mt-1 text-sm text-slate-500">
            The addresses email goes out from. Each sender belongs to one company and uses its own Resend API key (encrypted, never shown again).
          </p>
        </div>
        {isAdmin && !draft && <button className="btn-primary" onClick={() => { setDraft({ ...EMPTY }); setNotice(null); }}>Add sender</button>}
      </div>

      {error && <div className="card p-4 text-sm text-rose-700">{error}</div>}
      {notice && <div className="card p-4 text-sm text-emerald-700">{notice}</div>}
      {!isAdmin && <div className="card p-4 text-sm text-slate-600">Only an admin can add or change sender accounts.</div>}

      {draft && (
        <div className="card grid grid-cols-1 gap-3 p-5 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-600">Company</label>
            <select className="w-full !text-sm !py-2 md:w-1/2" value={draft.business_id}
                    onChange={e => setDraft({ ...draft, business_id: e.target.value ? Number(e.target.value) : "" })}>
              <option value="">Choose…</option>
              {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <Field label="Label (shown when sending)" value={draft.label} onChange={v => setDraft({ ...draft, label: v })} placeholder="e-Veritas sales" />
          <Field label="From name" value={draft.from_name} onChange={v => setDraft({ ...draft, from_name: v })} placeholder="Jane Smith" />
          <Field label="From email" value={draft.from_email} onChange={v => setDraft({ ...draft, from_email: v })} placeholder="jane@e-veritas.co" />
          <Field label="Reply-To (optional)" value={draft.reply_to} onChange={v => setDraft({ ...draft, reply_to: v })} placeholder="defaults to the from email" />
          <Field label={draft.id ? "Resend API key (leave blank to keep the current one)" : "Resend API key"} value={draft.resend_api_key}
                 onChange={v => setDraft({ ...draft, resend_api_key: v })} placeholder="re_…" secret />
          <Field label="Resend webhook signing secret (optional)" value={draft.webhook_secret}
                 onChange={v => setDraft({ ...draft, webhook_secret: v })} placeholder="whsec_… — for delivered / bounced tracking" secret />
          <p className="text-[11px] text-slate-500 md:col-span-2">
            The from-email's domain must be verified on the same Resend account as the key. For delivery tracking, point that account's webhook at
            <code className="mx-1">https://tools.mailbit.co.th/api/outreach/resend-webhook</code>and paste its signing secret above.
          </p>
          <div className="flex justify-end gap-2 md:col-span-2">
            <button className="btn-secondary" onClick={() => setDraft(null)}>Cancel</button>
            <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Checking key…" : "Save"}</button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {senders.length === 0
          ? <div className="p-6 text-center text-sm text-slate-500">
              No sender accounts yet. e-Veritas can send through the default e-veritas.co account; MailBIT and TwinShield need a sender.
            </div>
          : (
            <table className="refined">
              <thead><tr><th>Company</th><th>Label</th><th>From</th><th>Reply-To</th><th>Key</th><th>Webhook</th><th>Status</th>{isAdmin && <th></th>}</tr></thead>
              <tbody>
                {senders.map(s => (
                  <tr key={s.id} className={s.disabled ? "opacity-50" : ""}>
                    <td><span className="pill pill-slate">{companyName(s.business_id)}</span></td>
                    <td className="font-medium">{s.label}</td>
                    <td className="text-xs">{s.from_name && <>{s.from_name} </>}<span className="font-mono">&lt;{s.from_email}&gt;</span></td>
                    <td className="font-mono text-xs">{s.reply_to || "—"}</td>
                    <td className="font-mono text-xs">re_…{s.key_last4}</td>
                    <td className="text-xs">{s.has_webhook_secret ? "yes" : "—"}</td>
                    <td><span className={`pill ${s.disabled ? "pill-slate" : "pill-green"}`}>{s.disabled ? "disabled" : "active"}</span></td>
                    {isAdmin && (
                      <td className="whitespace-nowrap text-right text-xs">
                        <button className="text-indigo-600 hover:underline" onClick={() => setDraft({
                          id: s.id, business_id: s.business_id, label: s.label, from_name: s.from_name || "", from_email: s.from_email, reply_to: s.reply_to || "",
                          resend_api_key: "", webhook_secret: "",
                        })}>Edit</button>
                        <button className="ml-3 text-slate-500 hover:underline" onClick={async () => {
                          if (s.disabled) await research.updateSender(s.id, { disabled: false });
                          else if (confirm(`Disable "${s.label}"? Queued emails on it will fail.`)) await research.disableSender(s.id);
                          load();
                        }}>{s.disabled ? "Enable" : "Disable"}</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, secret }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; secret?: boolean }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <input type={secret ? "password" : "text"} autoComplete="off" className="w-full !text-sm !py-2 !px-3"
             value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </div>
  );
}
